import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check, Minus, Plus } from "lucide-react";

const triggerCls = "w-full h-11 rounded-lg px-3 text-sm bg-transparent inline-flex items-center justify-between gap-2 text-left";
const triggerStyle = (err) => ({ border: `1px solid ${err ? "#dc2626" : "var(--border-strong)"}` });

// Searchable dropdown that also accepts a typed value when it isn't in the list
export function SearchSelect({ value, onChange, options, placeholder, error, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  const custom = q.trim() && !options.some((o) => o.toLowerCase() === q.trim().toLowerCase());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} className={triggerCls} style={triggerStyle(error)} data-testid={testid}>
          <span className="truncate" style={value ? {} : { color: "var(--text-muted)" }}>{value || placeholder || "Select"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[220px]" align="start">
        <Command>
          <CommandInput placeholder="Search…" value={q} onValueChange={setQ} data-testid={`${testid}-search`}
                        className="focus:outline-none focus-visible:outline-none focus-visible:ring-0" style={{ outline: "none", boxShadow: "none" }} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)}>
                  <Check className={`h-4 w-4 mr-2 ${value === o ? "opacity-100" : "opacity-0"}`} />{o}
                </CommandItem>
              ))}
            </CommandGroup>
            {custom && (
              <CommandGroup>
                <CommandItem value={`__custom ${q}`} onSelect={() => pick(q.trim().slice(0, 80))} data-testid={`${testid}-custom`}>
                  Use “{q.trim().slice(0, 80)}”
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const BANDS = [{ k: "a", label: "Adults", sub: "12+ yrs", min: 1, max: 9 }, { k: "c", label: "Children", sub: "2–11 yrs", min: 0, max: 8 },
               { k: "i", label: "Infants", sub: "under 2", min: 0, max: 4 }];
export const travellersText = (t) => [
  `${t.a} Adult${t.a > 1 ? "s" : ""}`, t.c ? `${t.c} Child${t.c > 1 ? "ren" : ""}` : null, t.i ? `${t.i} Infant${t.i > 1 ? "s" : ""}` : null,
].filter(Boolean).join(", ");

export function TravellersPicker({ value, onChange, testid }) {
  const t = value || { a: 1, c: 0, i: 0 };
  const set = (k, d) => onChange({ ...t, [k]: t[k] + d });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={triggerCls} style={triggerStyle(false)} data-testid={testid}>
          <span className="truncate">{travellersText(t)}</span><ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" align="start">
        {BANDS.map((b) => (
          <div key={b.k} className="flex items-center justify-between">
            <div><div className="text-sm font-medium">{b.label}</div><div className="text-xs" style={{ color: "var(--text-muted)" }}>{b.sub}</div></div>
            <div className="flex items-center gap-2">
              <button type="button" aria-label={`Fewer ${b.label}`} disabled={t[b.k] <= b.min} onClick={() => set(b.k, -1)}
                      className="h-8 w-8 rounded-full grid place-items-center disabled:opacity-30" style={{ border: "1px solid var(--border-strong)" }}
                      data-testid={`${testid}-${b.k}-minus`}><Minus className="h-3.5 w-3.5" /></button>
              <span className="w-5 text-center text-sm font-semibold" data-testid={`${testid}-${b.k}-count`}>{t[b.k]}</span>
              <button type="button" aria-label={`More ${b.label}`} disabled={t[b.k] >= b.max} onClick={() => set(b.k, 1)}
                      className="h-8 w-8 rounded-full grid place-items-center disabled:opacity-30" style={{ border: "1px solid var(--border-strong)" }}
                      data-testid={`${testid}-${b.k}-plus`}><Plus className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
